import { isZhLocale } from '@yoyoclub/shared';
import Markdown from 'react-markdown';
import rehypeRaw from 'rehype-raw';
import rehypeSanitize from 'rehype-sanitize';
import remarkBreaks from 'remark-breaks';

import { toScript } from '../i18n/script';

const REMARK_BREAKS = [remarkBreaks];

interface HastNode {
  type: string;
  value?: string;
  children?: HastNode[];
}

/**
 * Converts rendered text nodes into the reader's Chinese script (PRD §5.1).
 * Runs after sanitize on the HTML tree, so link targets, image sources and
 * other attribute values are never touched.
 */
function rehypeScript(lang: string) {
  const walk = (node: HastNode) => {
    if (node.type === 'text' && typeof node.value === 'string') {
      node.value = toScript(node.value, lang);
    } else {
      node.children?.forEach(walk);
    }
  };
  return () => (tree: HastNode) => walk(tree);
}

/**
 * A host-written description as families see it: Markdown with raw HTML,
 * sanitized, Chinese text in the reader's script. Shared by the detail pages
 * and the form preview so the preview renders a pane exactly like the page.
 */
export function DescriptionMarkdown({
  children,
  lang,
  lineBreaks = false,
}: {
  /** The stored text of the side being shown. */
  children: string;
  /** Current site language; Chinese text is shown in its script. */
  lang?: string;
  /** Keep single line breaks (the course page does, activity and pass pages do not). */
  lineBreaks?: boolean;
}) {
  const rehypePlugins = isZhLocale(lang)
    ? [rehypeRaw, rehypeSanitize, rehypeScript(lang as string)]
    : [rehypeRaw, rehypeSanitize];
  return (
    <div className="description-content">
      <Markdown
        remarkPlugins={lineBreaks ? REMARK_BREAKS : undefined}
        rehypePlugins={rehypePlugins}
        components={{
          // Sanitize strips inline styles; re-apply a safe size cap so
          // editor-inserted images stay thumbnail-sized (not full width).
          img: (props) => (
            <img
              src={props.src}
              alt={props.alt ?? ''}
              style={{ maxWidth: '200px', width: '100%', height: 'auto' }}
            />
          ),
          // Links leave the page in a new tab; href is already sanitized.
          a: (props) => (
            <a href={props.href} target="_blank" rel="noopener noreferrer">
              {props.children}
            </a>
          ),
        }}
      >
        {children}
      </Markdown>
    </div>
  );
}
